import { createContext, useContext, useEffect, useMemo, useState, useRef, ReactNode, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { showErrorToast } from '@/src/renderer/components/common/MessageToast'
import { performSync, scheduleSync, setSyncActive } from '../lib/sync/sync-engine'
import { shouldRunBackgroundSync } from '../lib/sync/throttle'
import { getSyncQueueStats, clearSyncedItems } from '../lib/sync/sync-queue'
import { subscribeToSyncedTables, getSyncEntitlement, setSyncEntitlement as persistSyncEntitlement } from '../lib/supabase/queries'
import { isSupabaseAvailable } from '../lib/supabase/client'
import { checkSupabaseReachable } from '../lib/supabase/reachability'
import { useAuth } from './AuthProvider'
import { syncsOnItsOwn } from '../routes/standaloneWindowRoutes'
import { getPlatformAPI } from '../lib/platform'
import type { StorageMode } from '../lib/platform/types'
import { SYNC_IN_PROGRESS } from '../lib/sync/types'
import type { SyncState, SyncResult, SyncOptions } from '../lib/sync/types'

/**
 * Sync Context Provider
 * Manages sync state, auto-sync polling, and Supabase Realtime subscriptions
 */

interface SyncContextValue {
  syncState: SyncState
  isOnline: boolean
  isRealtimeConnected: boolean
  isSupabaseReachable: boolean
  nextRetryIn: number // seconds until next reconnect attempt

  // Sync actions
  sync: (options?: SyncOptions) => Promise<SyncResult>
  enableAutoSync: () => void
  disableAutoSync: () => void
  isAutoSyncEnabled: boolean

  // HappyCloud-Sync gate (storageMode): activate/deactivate online sync
  isSyncEnabled: boolean
  setSyncEnabled: (enabled: boolean) => void

  // Sync entitlement (paid add-on). Being authenticated does NOT imply sync —
  // the account must have booked/activated it. `setSyncEnabled(true)` is a no-op
  // without it.
  syncEntitlement: boolean
  /** `false` until the entitlement has been fetched for the current user — lets
   *  the web /account guard wait instead of flickering through the paywall. */
  entitlementLoaded: boolean
  /** Beta: self-activate the sync entitlement on the account. Returns success. */
  activateSync: () => Promise<boolean>
  /** Geräte-Sync läuft: angemeldet und gebucht. Nur dann wartet etwas auf die
   *  Verbindung — und nur dann zeigt sich das Offline-Symbol. */
  syncActive: boolean
}

/** Check if a sync error string indicates a network/connectivity problem */
function isNetworkError(error: string): boolean {
  const patterns = ['fetch', 'network', 'timeout', 'econnrefused', 'err_name', 'failed to fetch', 'load failed']
  const lower = error.toLowerCase()
  return patterns.some(p => lower.includes(p))
}

/**
 * Returns true when the sync result is "another sync is already running" —
 * not a real failure, just a deferral. The other trigger (focus-gained,
 * online, auto-sync interval) will complete the work, so wake-sync should
 * NOT retry and must not raise a user-visible toast.
 */
function isSyncDeferred(errors: readonly string[]): boolean {
  return errors.length > 0 && errors.every(e => e === SYNC_IN_PROGRESS)
}

// Der zuletzt bestätigte Stand von Geräte-Sync, je Konto. Er überbrückt nur die
// Zeit, in der die Cloud nicht antwortet — sonst hieße ein Start ohne Netz „nicht
// gebucht“ (25.10.2026). Entscheiden tut weiter die Cloud.
const entitlementMemoryKey = (userId: string) => `syncEntitlement:${userId}`

function recallSyncEntitlement(userId: string): boolean | null {
  try {
    const value = localStorage.getItem(entitlementMemoryKey(userId))
    return value == null ? null : value === 'true'
  } catch {
    return null
  }
}

function rememberSyncEntitlement(userId: string, entitled: boolean): void {
  try {
    localStorage.setItem(entitlementMemoryKey(userId), String(entitled))
  } catch {
    // Gesperrter Speicher: Dann gibt es eben keine Brücke für den nächsten Start.
  }
}

// Exported for components that render outside the provider tree (e.g. Titlebar)
// and need safe access via useContext() which returns undefined instead of throwing.
export const SyncContext = createContext<SyncContextValue | undefined>(undefined)

/**
 * Geräte-Sync an, Netz da, aber der erste Sync ist noch nicht durch: Das Gerät kennt den Stand der Cloud
 * noch nicht — leer ist es dann nur scheinbar. Scheitert der Sync oder fehlt das Netz, wartet nichts.
 * Ohne Provider (Storybook, Tests) auch nicht.
 */
export function isAwaitingFirstSync(context: Pick<SyncContextValue, 'isSyncEnabled' | 'isOnline' | 'syncState'> | undefined): boolean {
  if (!context?.isSyncEnabled || !context.isOnline) return false
  const { status, lastSyncAt } = context.syncState
  return !lastSyncAt && status !== 'error' && status !== 'offline'
}

interface SyncProviderProps {
  children: ReactNode
  autoSyncInterval?: number // in milliseconds (default: 5 minutes)
}

export function SyncProvider({ children, autoSyncInterval = 5 * 60 * 1000 }: SyncProviderProps) {
  const { t } = useTranslation()
  const { user, isAuthenticated } = useAuth()
  const lastSyncAtRef = useRef<Date | null>(null)
  // Last time a background-freshness trigger (wake/focus) *attempted* a sync.
  // Keyed on attempt, not completion, so a slow in-flight sync still throttles
  // piled-up triggers — see shouldRunBackgroundSync.
  const lastBackgroundSyncAtRef = useRef(0)
  const [syncState, setSyncState] = useState<SyncState>({
    status: 'idle',
    pendingChanges: 0,
  })
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const isOnlineRef = useRef(isOnline)
  isOnlineRef.current = isOnline
  const [isAutoSyncEnabled, setIsAutoSyncEnabled] = useState(false)
  const autoSyncTimerRef = useRef<NodeJS.Timeout | null>(null)
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false)
  const [isSupabaseReachable, setIsSupabaseReachable] = useState(true)
  const [nextRetryIn, setNextRetryIn] = useState(0)

  // HappyCloud-Sync gate: 'cloud-sync' = online sync active, 'offline' = local-only.
  // Offline-first default until the async load resolves.
  const [storageMode, setStorageModeState] = useState<StorageMode>('offline')
  const isSyncEnabled = storageMode === 'cloud-sync'

  // Sync entitlement (paid add-on). Loaded from the account profile; false until
  // resolved and whenever logged out. `entitlementLoaded` distinguishes "still
  // fetching" from "fetched: not entitled" so the web /account guard can show a
  // spinner instead of flickering an entitled user through the paywall.
  // `entitlementTick` forces a re-fetch (cross-window activation / focus).
  const [syncEntitlement, setSyncEntitlementState] = useState(false)
  const [entitlementLoaded, setEntitlementLoaded] = useState(false)
  const [entitlementTick, setEntitlementTick] = useState(0)

  // The cloud `sync_entitlement` is the single source of truth for sync on/off.
  // Sync runs whenever the account is authenticated AND entitled. We deliberately
  // do NOT also require the local `storageMode` here: it lives in Dexie and is
  // wiped on logout, which made the switch reset to off after every logout/login
  // even though the DB said entitled. The switch (SyncPage) and this gate both
  // read the entitlement from the DB instead.
  const canSync = isAuthenticated && syncEntitlement
  // Die versteckte Schnelleingabe fährt keinen eigenen Sync (#234) — das Hauptfenster schiebt hoch.
  const ownSync = useMemo(() => syncsOnItsOwn(), [])
  const syncActive = canSync && ownSync

  // Die Nachfrage bei der Cloud entscheidet über `isSupabaseReachable`, nicht der
  // Sync: Der kann aus anderen Gründen scheitern (läuft noch, Datenfehler) und
  // hielte das Offline-Symbol dann fest, obwohl die Cloud antwortet. Zuruf,
  // „online“ und Countdown können gleichzeitig fragen — es geht nur eine Anfrage raus.
  const probeRef = useRef<Promise<boolean> | null>(null)
  const probeReachability = useCallback((): Promise<boolean> => {
    if (!probeRef.current) {
      probeRef.current = checkSupabaseReachable()
        .then((reachable) => {
          setIsSupabaseReachable(reachable)
          return reachable
        })
        .finally(() => { probeRef.current = null })
    }
    return probeRef.current
  }, [])

  // Was die Schnelleingabe angelegt hat, steht in der Warteschlange; hier geht es hoch.
  useEffect(() => {
    if (!syncActive) return
    return window.windowAPI?.onWindowMessage?.('quick-add:created', () => scheduleSync())
  }, [syncActive])

  // Load the persisted storage mode on mount.
  useEffect(() => {
    let cancelled = false
    getPlatformAPI()
      .getStorageMode()
      .then((mode) => {
        if (!cancelled) setStorageModeState(mode)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  // Load the sync entitlement from the account profile. Re-runs on auth change
  // and whenever `entitlementTick` bumps (cross-window activation, see the
  // storage-mode-changed handler below).
  const entitlementUserId = isAuthenticated ? user?.id ?? null : null
  // Which user the entitlement has been loaded for. Lets us distinguish a real
  // first/changed-user load from a background re-validation (entitlementTick).
  const entitlementLoadedForRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    let cancelled = false
    if (!entitlementUserId) {
      setSyncEntitlementState(false)
      setEntitlementLoaded(true)
      entitlementLoadedForRef.current = null
      return
    }
    // Only drop into the loading gate (entitlementLoaded=false) on a genuine first
    // or changed-user load. A focus-driven re-validation (entitlementTick bump)
    // must NOT reset it — otherwise ProtectedRoute flips to HappyTaskLoadingLogo
    // and REMOUNTS the whole app (MainLayout + titlebar) on every window focus,
    // which users saw as "the content refreshes + the app logo flashes on focus".
    // The re-fetch still runs and updates `syncEntitlement` silently in the bg.
    const isFirstLoadForUser = entitlementLoadedForRef.current !== entitlementUserId
    entitlementLoadedForRef.current = entitlementUserId
    if (isFirstLoadForUser) {
      setEntitlementLoaded(false)
      // Bis die Cloud antwortet, gilt der zuletzt bestätigte Stand dieses Geräts.
      // Die Web-Sperre wartet trotzdem auf `entitlementLoaded`.
      setSyncEntitlementState(recallSyncEntitlement(entitlementUserId) ?? false)
    }
    getSyncEntitlement(entitlementUserId)
      .then((entitled) => {
        rememberSyncEntitlement(entitlementUserId, entitled)
        if (!cancelled) {
          setSyncEntitlementState(entitled)
          setEntitlementLoaded(true)
        }
      })
      .catch(() => {
        // No answer: keep the prior (or remembered) entitlement — don't downgrade
        // an entitled user to the paywall over a transient network error.
        if (!cancelled) setEntitlementLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [entitlementUserId, entitlementTick])

  // Cross-window (Electron): the HappyCloud-Sync toggle lives in the standalone
  // preferences window, which has its own React root without this provider.
  // Listen for the `storage-mode-changed` broadcast so the sync gate updates
  // live without an app restart.
  useEffect(() => {
    if (!window.api?.receive) return
    const cleanup = window.api.receive('storage-mode-changed', (mode: string) => {
      setStorageModeState(mode as StorageMode)
      // The prefs window may have just activated the entitlement before flipping
      // the toggle — re-fetch so the gate sees the fresh value, not a stale false.
      setEntitlementTick((n) => n + 1)
    })
    return () => {
      cleanup?.()
    }
  }, [])

  // Re-validate the entitlement when the window regains focus, so a server-side
  // change (plan added/revoked) is picked up without a restart. Cheap profiles
  // read; no-ops when logged out (the entitlement effect bails on a null user).
  useEffect(() => {
    const revalidate = () => {
      if (document.visibilityState === 'visible') setEntitlementTick((n) => n + 1)
    }
    document.addEventListener('visibilitychange', revalidate)
    window.addEventListener('focus', revalidate)
    return () => {
      document.removeEventListener('visibilitychange', revalidate)
      window.removeEventListener('focus', revalidate)
    }
  }, [])

  // Keep latest auth/auto-sync flags in a ref so the online/offline listener
  // can read them without re-subscribing on every state tick.
  const listenerStateRef = useRef({ isAuthenticated, isAutoSyncEnabled, syncActive })
  listenerStateRef.current = { isAuthenticated, isAutoSyncEnabled, syncActive }

  // Monitor online/offline status — attach once for the provider's lifetime.
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)

      const { isAuthenticated: authed, isAutoSyncEnabled: autoOn, syncActive: active } = listenerStateRef.current
      // Das Netz ist zurück: gleich nachfragen, nicht erst nach dem Countdown.
      if (active) void probeReachability()

      // Trigger sync when coming back online
      if (authed && autoOn) {
        syncOpRef.current()
      }
    }

    const handleOffline = () => {
      setIsOnline(false)
      setIsSupabaseReachable(false)
      setSyncState((prev) => ({ ...prev, status: 'offline' }))
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [probeReachability])

  // Sync on tab/window refocus after inactivity (e.g. returning to a tab left
  // open overnight). Only triggers if this long has passed since the last
  // successful sync. 30s (was 5s): a brief app/window switch shouldn't fire a
  // full 7-table pull on every return — users perceived that as the content
  // "refreshing" on focus. Realtime + the 5-min interval keep data fresh between.
  const STALE_SYNC_THRESHOLD_MS = 30_000
  // Min gap between Realtime-triggered syncs. Supabase reflects our own pushes
  // back as Realtime events; without this guard each push echoes into a new sync
  // (which pushes again → echo → loop, observed as a sync every few seconds).
  // Events are deferred (coalesced), not dropped, so genuine remote changes
  // still sync — with at most this much delay.
  const REALTIME_MIN_GAP_MS = 10_000
  // Wake-up resilience: with OS background throttling active, the first sync
  // attempt right after visibilitychange can hit a half-thawed network stack.
  // A small grace period lets sockets/DNS warm up; a silent retry absorbs any
  // transient failure so the user only sees a toast on a real outage.
  const WAKE_SYNC_GRACE_MS = 800
  const WAKE_SYNC_RETRY_DELAY_MS = 1500
  // Keep latest `t` in a ref so the listener reads the current translator
  // without re-subscribing when the language changes.
  const tRef = useRef(t)
  tRef.current = t
  useEffect(() => {
    let graceTimer: ReturnType<typeof setTimeout> | null = null
    let retryTimer: ReturnType<typeof setTimeout> | null = null

    const attemptWakeSync = async (isRetry: boolean): Promise<void> => {
      // Skip entirely if we know we're offline. The online event handler will
      // trigger a sync as soon as connectivity returns, so no user-visible toast.
      if (!navigator.onLine) return

      try {
        const result = await syncOpRef.current()
        if (result.success) return
        // Another trigger (focus-gained, online, interval) is already running
        // a sync. Our goal is met — skip retry and toast entirely.
        if (isSyncDeferred(result.errors)) return
        if (!isRetry) {
          retryTimer = setTimeout(() => {
            retryTimer = null
            attemptWakeSync(true)
          }, WAKE_SYNC_RETRY_DELAY_MS)
          return
        }
        // Retry also failed. Suppress the toast if the failure clearly stems
        // from the network (user already knows — e.g. disabled Wi-Fi). The
        // online event will re-sync once connectivity returns.
        if (!navigator.onLine || result.errors.some(isNetworkError)) return
        showErrorToast(tRef.current('sync.resumeFailedTitle'), { description: tRef.current('sync.resumeFailedText') })
      } catch (error) {
        if (!isRetry) {
          retryTimer = setTimeout(() => {
            retryTimer = null
            attemptWakeSync(true)
          }, WAKE_SYNC_RETRY_DELAY_MS)
          return
        }
        if (!navigator.onLine || isNetworkError(String(error))) return
        showErrorToast(tRef.current('sync.resumeFailedTitle'), { description: tRef.current('sync.resumeFailedText') })
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        // Leaving the tab — cancel any pending wake-sync so it doesn't fire
        // in the background and defeat the point of OS throttling.
        if (graceTimer) { clearTimeout(graceTimer); graceTimer = null }
        if (retryTimer) { clearTimeout(retryTimer); retryTimer = null }
        return
      }
      const { isAuthenticated: authed, isAutoSyncEnabled: autoOn } = listenerStateRef.current
      if (!authed || !autoOn) return

      if (
        !shouldRunBackgroundSync(
          Date.now(),
          lastBackgroundSyncAtRef.current,
          lastSyncAtRef.current?.getTime() ?? null,
          STALE_SYNC_THRESHOLD_MS,
        )
      ) {
        return
      }
      // Stamp the attempt now (before the grace delay) so a focus event during
      // the grace window coalesces into this one instead of firing its own sync.
      lastBackgroundSyncAtRef.current = Date.now()

      // Grace period: give the OS a moment to thaw the network stack before
      // hitting Supabase.
      if (graceTimer) clearTimeout(graceTimer)
      graceTimer = setTimeout(() => {
        graceTimer = null
        attemptWakeSync(false)
      }, WAKE_SYNC_GRACE_MS)
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (graceTimer) clearTimeout(graceTimer)
      if (retryTimer) clearTimeout(retryTimer)
    }
  }, [])

  // Update pending changes count
  useEffect(() => {
    const updatePendingCount = async () => {
      const stats = await getSyncQueueStats()
      setSyncState((prev) => ({ ...prev, pendingChanges: stats.pending }))
    }

    updatePendingCount()

    // Update every 30 seconds
    const interval = setInterval(updatePendingCount, 30000)
    return () => clearInterval(interval)
  }, [])

  // Perform sync operation
  const performSyncOperation = useCallback(
    async (options?: SyncOptions): Promise<SyncResult> => {
      // Can't sync if not authenticated
      if (!isAuthenticated || !user) {
        return {
          success: false,
          synced: { created: 0, updated: 0, deleted: 0 },
          conflicts: [],
          errors: ['Not authenticated'],
        }
      }

      // Can't sync if offline
      if (!isOnline) {
        setSyncState((prev) => ({ ...prev, status: 'offline' }))
        return {
          success: false,
          synced: { created: 0, updated: 0, deleted: 0 },
          conflicts: [],
          errors: ['Offline'],
        }
      }

      try {
        setSyncState((prev) => ({ ...prev, status: 'syncing', error: undefined }))

        const result = await performSync(options)

        if (result.success) {
          lastSyncAtRef.current = new Date()
          setIsSupabaseReachable(true)
          setSyncState({
            status: 'synced',
            lastSyncAt: lastSyncAtRef.current,
            pendingChanges: 0,
          })

          // Cleanup old synced items
          await clearSyncedItems(7)
        } else {
          // Check if failure is due to network/connectivity
          if (result.errors.some(e => isNetworkError(e))) {
            console.warn('[SyncProvider] 🔴 Supabase unreachable — sync errors:', result.errors)
            setIsSupabaseReachable(false)
          }
          setSyncState((prev) => ({
            ...prev,
            status: 'error',
            error: result.errors.join(', '),
          }))
        }

        return result
      } catch (error) {
        const errorStr = String(error)
        if (isNetworkError(errorStr)) {
          console.warn('[SyncProvider] 🔴 Supabase unreachable — caught error:', errorStr)
          setIsSupabaseReachable(false)
        }
        setSyncState((prev) => ({
          ...prev,
          status: 'error',
          error: errorStr,
        }))

        return {
          success: false,
          synced: { created: 0, updated: 0, deleted: 0 },
          conflicts: [],
          errors: [errorStr],
        }
      }
    },
    [isAuthenticated, user, isOnline]
  )

  // Enable auto-sync
  const enableAutoSync = useCallback(() => {
    if (!isAuthenticated) {
      console.warn('[Sync] Cannot enable auto-sync: not authenticated')
      return
    }

    setIsAutoSyncEnabled(true)

    // Clear existing timer
    if (autoSyncTimerRef.current) {
      clearInterval(autoSyncTimerRef.current)
    }

    // Run the regular sync. Local-only records (offline-created tasks/tags/
    // projects) are uploaded by performSync's queue + name-dedup stage — no
    // separate initialSync pass (which bypassed the dedup and duplicated tags).
    syncOpRef.current()

    // Set up periodic sync (use refs to avoid stale closures in long-lived interval)
    autoSyncTimerRef.current = setInterval(() => {
      if (isOnlineRef.current) {
        syncOpRef.current()
      }
    }, autoSyncInterval)
  }, [isAuthenticated, autoSyncInterval])

  // Disable auto-sync
  const disableAutoSync = useCallback(() => {
    setIsAutoSyncEnabled(false)

    if (autoSyncTimerRef.current) {
      clearInterval(autoSyncTimerRef.current)
      autoSyncTimerRef.current = null
    }

  }, [])

  // Activate/deactivate HappyCloud-Sync. Persists the storageMode; the gate
  // effect below reacts to the change to start/stop syncing. Local data is
  // untouched when deactivating.
  const setSyncEnabled = useCallback(async (enabled: boolean) => {
    // Enabling requires an active entitlement — sync is a paid add-on. Disabling
    // is always allowed.
    if (enabled && !(isAuthenticated && syncEntitlement)) {
      console.warn('[Sync] Cannot enable HappyCloud-Sync without an entitlement')
      return
    }
    const mode: StorageMode = enabled ? 'cloud-sync' : 'offline'
    try {
      await getPlatformAPI().setStorageMode(mode)
    } catch (err) {
      console.error('[Sync] Failed to persist storage mode:', err)
    }
    setStorageModeState(mode)
  }, [isAuthenticated, syncEntitlement])

  // Beta self-activation: write the entitlement to the account profile, then
  // reflect it locally. Real billing later moves this behind a payment webhook.
  const activateSync = useCallback(async (): Promise<boolean> => {
    if (!isAuthenticated || !user) return false
    const ok = await persistSyncEntitlement(user.id, true)
    if (ok) {
      rememberSyncEntitlement(user.id, true)
      setSyncEntitlementState(true)
      setEntitlementLoaded(true)
    }
    return ok
  }, [isAuthenticated, user])

  // Gate sync on auth + entitlement + HappyCloud-Sync mode. Also report the gate
  // to the sync engine so a mutation's queue entry no-ops while in
  // local-only mode.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      // Heal a stale offline storageMode for an entitled account BEFORE reporting
      // the gate / starting auto-sync. The entitlement may have been granted via a
      // path that never touched THIS device's mode — the web account hub
      // (activateSync), another device, or a direct DB write — leaving the persisted
      // local mode 'offline'. The sync engine's performSync gates on the
      // Dexie-backed storageMode (sync-engine.ts), so without this upgrade the first
      // pull silently no-ops and the local DB (and UI) stay empty despite an
      // entitled, populated account. We read the persisted value authoritatively
      // (not the possibly-stale React state) to avoid racing the mount-time load
      // effect. Upgrade-only: the downgrade direction deliberately stays out (see
      // the note below) so the toggle isn't reset during the brief unentitled window
      // while the Supabase session hydrates on startup.
      if (syncActive) {
        try {
          if ((await getPlatformAPI().getStorageMode()) === 'offline') {
            await getPlatformAPI().setStorageMode('cloud-sync')
            if (!cancelled) setStorageModeState('cloud-sync')
          }
        } catch (err) {
          console.error('[Sync] Failed to reconcile storage mode to cloud-sync:', err)
        }
      }
      if (cancelled) return

      setSyncActive(syncActive)
      if (syncActive && !isAutoSyncEnabled) {
        enableAutoSync()
      } else if (!syncActive && isAutoSyncEnabled) {
        // Stop syncing when logged out, not entitled, or HappyCloud-Sync deactivated
        disableAutoSync()
      }
    })()
    return () => {
      cancelled = true
    }
    // Nur auf syncActive reagieren: Hinge der Effect auch am Auto-Sync-Zustand,
    // schaltete er sich über enable/disableAutoSync selbst erneut an.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncActive])

  // NOTE: we deliberately do NOT auto-downgrade a persisted 'cloud-sync' mode to
  // 'offline' when the account isn't entitled. The `syncActive` gate already
  // prevents any syncing without an entitlement, and the Settings page shows the
  // "Activate" CTA (not the toggle) when unentitled. Rewriting storageMode here
  // was destructive: during startup, while the Supabase session hydrates,
  // entitlement briefly reads false and the downgrade flipped + persisted
  // 'offline' — so the sync toggle reset itself to off after every restart.

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoSyncTimerRef.current) {
        clearInterval(autoSyncTimerRef.current)
      }
    }
  }, [])

  // ==================== REALTIME SUBSCRIPTIONS ====================

  // Ref to hold the latest performSyncOperation (avoids stale closure in debounce)
  const syncOpRef = useRef(performSyncOperation)
  syncOpRef.current = performSyncOperation

  // Debounced sync triggered by Realtime events
  const realtimeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const triggerRealtimeSync = useCallback(() => {
    // Debounce (300ms) + min-gap coalescing: batch rapid Realtime events and
    // never sync more than once per REALTIME_MIN_GAP_MS. Breaks the echo loop
    // where our own push is reflected back as a Realtime event. Deferring (not
    // dropping) keeps eventual consistency for genuine remote changes.
    if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current)
    const run = () => {
      const elapsed = lastSyncAtRef.current
        ? Date.now() - lastSyncAtRef.current.getTime()
        : Infinity
      if (elapsed < REALTIME_MIN_GAP_MS) {
        // Too soon after the last sync (likely an echo of our own push) — defer
        // until the gap elapses, coalescing any further events into one sync.
        realtimeDebounceRef.current = setTimeout(run, REALTIME_MIN_GAP_MS - elapsed)
        return
      }
      realtimeDebounceRef.current = null
      syncOpRef.current().catch((err) =>
        console.error('[Realtime] Sync triggered by Realtime failed:', err)
      )
    }
    realtimeDebounceRef.current = setTimeout(run, 300)
  }, [REALTIME_MIN_GAP_MS])

  // Track previous Realtime connection state for reconnect catch-up
  const wasRealtimeConnectedRef = useRef(false)

  useEffect(() => {
    if (isRealtimeConnected && !wasRealtimeConnectedRef.current) {
      // Just reconnected after a disconnect — catch up on missed events
      syncOpRef.current().catch(() => {})
    }
    wasRealtimeConnectedRef.current = isRealtimeConnected
  }, [isRealtimeConnected])

  // Subscribe to Supabase Realtime channels only when sync is active
  useEffect(() => {
    if (!syncActive || !user?.id || !isSupabaseAvailable()) {
      setIsRealtimeConnected(false)
      return
    }

    // EIN Kanal für alle synchronisierten Tabellen (ADR 0017). Vorher waren es
    // drei für drei Tabellen — die übrigen acht riefen nie zu, und ihre
    // Änderungen warteten auf der Gegenseite bis zum 5-Minuten-Intervall.
    const unsubscribe = subscribeToSyncedTables(
      user.id,
      triggerRealtimeSync,
      (status) => {
        const connected = status === 'SUBSCRIBED'
        setIsRealtimeConnected(connected)
        if (connected) setIsSupabaseReachable(true)
        // Ein abgerissener Zuruf allein heißt nicht offline: Er reißt oft für
        // ein, zwei Sekunden ab, während der Sync weiterläuft. Ob die Cloud
        // fehlt, sagt erst die Nachfrage.
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') void probeReachability()
      }
    )

    return () => {
      unsubscribe()
      setIsRealtimeConnected(false)
      if (realtimeDebounceRef.current) {
        clearTimeout(realtimeDebounceRef.current)
      }
    }
  }, [syncActive, user?.id, triggerRealtimeSync, probeReachability])

  // ==================== FOCUS SYNC (Electron) ====================

  // Sync when the Electron window regains focus after inactivity
  const focusSyncDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!isAuthenticated || !isAutoSyncEnabled) return

    const handleFocusGained = () => {
      // Debounce: max one focus-sync per 2 seconds
      if (focusSyncDebounceRef.current) return

      // Skip if a background sync ran (or is in-flight) recently — shared with the
      // visibilitychange handler so wake+focus coalesce instead of double-firing.
      if (
        !shouldRunBackgroundSync(
          Date.now(),
          lastBackgroundSyncAtRef.current,
          lastSyncAtRef.current?.getTime() ?? null,
          STALE_SYNC_THRESHOLD_MS,
        )
      ) {
        return
      }
      lastBackgroundSyncAtRef.current = Date.now()

      focusSyncDebounceRef.current = setTimeout(() => {
        focusSyncDebounceRef.current = null
      }, 2000)

      syncOpRef.current().catch(() => {})
    }

    // Electron: listen for IPC from main process
    let cleanup: (() => void) | undefined
    if (window.api?.receive) {
      cleanup = window.api.receive('window:focus-gained', handleFocusGained) || undefined
    }

    return () => {
      cleanup?.()
      if (focusSyncDebounceRef.current) {
        clearTimeout(focusSyncDebounceRef.current)
        focusSyncDebounceRef.current = null
      }
    }
  }, [isAuthenticated, isAutoSyncEnabled])

  // ==================== RETRY COUNTDOWN ====================

  // When offline/unreachable, count down to next reconnect attempt
  const RETRY_INTERVAL = 30 // seconds
  useEffect(() => {
    const isDisconnected = !isOnline || !isSupabaseReachable
    if (!isDisconnected || !syncActive) {
      setNextRetryIn(0)
      return
    }

    // Der Zähler lebt im Effekt, nicht im State-Updater: Ein Updater mit
    // Nebenwirkung liefe im StrictMode doppelt und fragte zweimal nach.
    let remaining = RETRY_INTERVAL
    setNextRetryIn(remaining)
    const interval = setInterval(() => {
      remaining -= 1
      if (remaining <= 0) {
        remaining = RETRY_INTERVAL
        // Erst nachfragen, dann synchronisieren: Nur die Antwort der Cloud sagt,
        // ob sie wieder da ist — der Sync kann aus anderen Gründen scheitern.
        void probeReachability().then((reachable) => {
          if (reachable) syncOpRef.current().catch(() => {})
        })
      }
      setNextRetryIn(remaining)
    }, 1000)

    return () => clearInterval(interval)
  }, [isOnline, isSupabaseReachable, syncActive, probeReachability])

  const value = useMemo<SyncContextValue>(() => ({
    syncState,
    isOnline,
    isRealtimeConnected,
    isSupabaseReachable,
    nextRetryIn,
    sync: performSyncOperation,
    enableAutoSync,
    disableAutoSync,
    isAutoSyncEnabled,
    isSyncEnabled,
    setSyncEnabled,
    syncEntitlement,
    entitlementLoaded,
    activateSync,
    syncActive,
  }), [
    syncState,
    isOnline,
    isRealtimeConnected,
    isSupabaseReachable,
    nextRetryIn,
    performSyncOperation,
    enableAutoSync,
    disableAutoSync,
    isAutoSyncEnabled,
    isSyncEnabled,
    setSyncEnabled,
    syncEntitlement,
    entitlementLoaded,
    activateSync,
    syncActive,
  ])

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>
}

// Custom hook to use sync context
export function useSync(): SyncContextValue {
  const context = useContext(SyncContext)

  if (context === undefined) {
    throw new Error('useSync must be used within a SyncProvider')
  }

  return context
}
